import "./App.css";
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useSearchParams,
  Navigate
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "../src/services/queryClient";
import { AuthProvider } from "./context/AuthContext";
import { PrivateRoute, PublicRoute } from "./auth/ProtectedRoute";
import { NotificationProvider } from "./context/NotificationContext";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import CreatePost from "./pages/CreatePost";
import Feed from "./pages/Feed";
import Explore from "./pages/Explore";
import Settings from "./pages/Settings";
import FeedPage from "./pages/FeedPage";
import PostDetailPage from "./pages/PostDetailPage";
import NotificationPage from "./pages/NotificationPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import ForgotPassword from "./components/Login/ForgotPassword";
import Trending from "./pages/Trending";
import InvalidFeedType from "./pages/InvalidFeedType";
import NotFound from "./pages/NotFound";
import PolicyPage from "./pages/PolicyPage";
import AboutPage from "./pages/AboutPage";
import ReviewRequests from "./pages/ReviewRequests";

const VALID_FEED_TYPES = ["for_you", "Poetry", "Shayari", "Ghazal", "Song", "Rap", "Hip-Hop", "Novel", "Story", "Book", "Script", "Quote"];

const FeedRouteWrapper = () => {
  const [searchParams] = useSearchParams();
  const feedType = searchParams.get("type");

  if (!feedType) {
    return <Navigate to="/feed?type=for_you" replace />;
  }

  return VALID_FEED_TYPES.includes(feedType) ? <Feed /> : <InvalidFeedType />;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Oops! Something went wrong</h1>
            <p className="text-gray-600 mb-4">We're sorry, but something unexpected happened.</p>
            <button onClick={() => window.location.reload()} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <NotificationProvider>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/policy" element={<PolicyPage />} />
                <Route path="/about" element={<AboutPage />} />
                {/* Protected Routes - Fixed Order */}
                <Route path="/feed" element={<PrivateRoute><FeedRouteWrapper /></PrivateRoute>} />
                <Route path="/home" element={<PrivateRoute><FeedPage /></PrivateRoute>} />
                <Route path="/explore" element={<PrivateRoute><Explore /></PrivateRoute>} />
                <Route path="/trending" element={<PrivateRoute><Trending /></PrivateRoute>} />
                <Route path="/notifications" element={<PrivateRoute><NotificationPage /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                <Route path="/edit-profile" element={<PrivateRoute><EditProfile /></PrivateRoute>} />
                <Route path="/create-post" element={<PrivateRoute><CreatePost /></PrivateRoute>} />
                <Route path="/r/review-requests" element={<PrivateRoute><ReviewRequests /></PrivateRoute>} />
                <Route path="/collection/:type/p/:postId" element={<PrivateRoute><CollectionDetailPage /></PrivateRoute>} />

                {/* Dynamic/Parametric Routes (Keep at bottom) */}
                <Route path="/p/:postId" element={<PrivateRoute><PostDetailPage /></PrivateRoute>} />
                <Route path="/:username" element={<PrivateRoute><Profile /></PrivateRoute>} />

                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </NotificationProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;